import { useAdOperatorReportIo } from '@backoffice-frontend/ad-operator-report-io'
import {
  useErrorHandlingMutation,
  useErrorHandlingSubscription,
} from '@backoffice-frontend/data-fetching'
import {
  FccAdAlertType,
  FccServiceCancellationStateEnum,
} from '@backoffice-frontend/graphql'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import { CreationContext } from '@backoffice-frontend/report-io-lib'
import { useMachine } from '@xstate/react'
import { type ReactNode, createContext, useEffect } from 'react'
import { assign, setup } from 'xstate'
import {
  type AdVehicleStreamServiceCancellationVehicleFragment,
  useAdVehicleStreamConfirmCancelAllTripsMutation,
  useAdVehicleStreamContinueTripsMutation,
  useAdVehicleStreamInitiateServiceCancellationMutation,
  useAdVehicleStreamServiceCancellationStateSubscription,
} from './ServiceCancellation.hook'

type ServiceCancellationValue = {
  state: ServiceCancellationMachineState
  decision?: ServiceCancellationDecision
  initiate: VoidFunction
  abandon: VoidFunction
  cancelAllTrips: VoidFunction
  continueAllTrips: VoidFunction
}

export const ServiceCancellationContext = createContext<
  ServiceCancellationValue | undefined
>(undefined)

export type ServiceCancellationDecision = 'cancelAllTrips' | 'continueAllTrips'

type ServiceCancellationProviderProps = {
  vehicle: AdVehicleStreamServiceCancellationVehicleFragment
  children: ReactNode
}

const serviceCancellationStateMachine = setup({
  types: {
    events: {} as
      | { type: 'INITIATE' }
      | { type: 'ABANDON' }
      | { type: 'CANCEL_ALL_TRIPS' }
      | { type: 'CONTINUE_ALL_TRIPS' }
      | { type: 'CANCELLATION_SUCCESS' }
      | { type: 'CANCELLATION_ERROR' },
    context: {} as {
      lastConfirmationAction?: ServiceCancellationDecision
    },
  },
  actions: {
    initiateCancellation: () => {},
    confirmCancelAllTrips: () => {},
    confirmContinueAllTrips: () => {},
    onSuccess: () => {},
    onError: () => {},
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOlwgBswBiASQDlaAVWgQSYFEBtABgF1EoAA4B7WLgAuuEfkEgAHogC0ARgDMADhIqVPACxqA7IaMBOAKwaANCACey3XpJqATDwBs5nqY2XDKlz0AXyCbNCw8QlICSVx0CUhqVgAhVnoAEQB5el4BJBBRcSkZOUUEJRdtX1M9Cw19FxdfExt7csdnN09vXw1-QJCwjBwCYjJ8WPjEgGE06Y4AGQB9VgXlpgAlWgAFAGVcuULYkvyypTVvbRUNNT1LAxqVQ1bEXR4SQz13Rv9LDSe1O5BiBwiMouNJgkINRptkWPQAKocFZrJabHb7fiHMTHWSnZSGLSElxqTRqUz+QHmFQvBBvD5fNz-NRvNTmIGhEHDSJjTAyABmuAATqgZnNFgt2LRsktdgjpvNdpi8sIccU8aAzqSnG4DDw+iSap93LS9C5zCQ9CofIZ3Dd-uY7uZgaCeaQ+fhBSKxfR5mspTKOBsNpkNgd8kd1aVlBonID9OynoYmonTebLda+nbNCpHeZnZzXaN3QLhaLoSk0lkcliI2rpBqFK8XIYroFagZjDxzGpaap9J0PIZ9b5yb4OUMIsWSHzUEIqFCkqkMtlw6qig3o+U9ITOiYXO4PFbbba+x1XEORz2fAXJ2DeSI5wufX6Uestns1wV6ydNYhAq2bgBGSO6GhYtLuCoOr-Po3ymI0ph2i63LTrO85gIusL0PCSJvmiH7KtiG6-k2CA-JaFw6CYATJjwLi0j2WjmJ8ej6Lapi5t4IScvgIgQHAchFlERG4luSjmAee6uIe7jHnafYHu8pimK4pIeO4hj3MhU7guQVAiVG+LtNaJAaBxdpWohng6DSdgOCYpnKTuLIuIhUG2tp97RBMUhTBABmbkZqi3B8mYBMB+ZGApajODBbmOiysnsp5bozqW3r+XWxGNlqvgUTuljXBYtQHrS1yVJZbKaAe1KHi4KWoel5Yzug+CYGAFCsBQFBMEKuBCLA0xNZAAUkVqRj5Zp-xmY68EmnZZHuKYJDUhcpg8Co3x2q5wSFih4Iel6zUelI+AAK5gF1PV9QNQ2emWI1ZaJQX+KZzHGO47iuKBKm0gey2rd4G1bRoO0NQdj7oVCo05copIqCQPC3J9+YWDongMUjJAWbmuhLZpJLgw+T4YZAJCwGdmDtbA8BPYZf7lE0MVI18njmGjm3mAx60fMYKnmvDm0aETJYk1CJBgEKQoiEKMNbk4CbeAYO5POy4ELez7zGIY-M9iym2mNxQRAA */
  initial: 'idle',
  context: {
    lastConfirmationAction: undefined,
  },
  states: {
    idle: {
      on: {
        INITIATE: 'initiated',
      },
    },

    initiated: {
      entry: 'initiateCancellation',
      on: {
        ABANDON: 'idle',
        CANCEL_ALL_TRIPS: 'confirmed.cancelAllTripsConfirmed',
        CONTINUE_ALL_TRIPS: 'confirmed.continueAllTripsConfirmed',
      },
    },

    confirmed: {
      initial: 'cancelAllTripsConfirmed',
      on: {
        CANCELLATION_SUCCESS: 'completed.success',
        CANCELLATION_ERROR: 'completed.error',
        ABANDON: 'idle',
      },
      states: {
        cancelAllTripsConfirmed: {
          entry: 'confirmCancelAllTrips',
        },
        continueAllTripsConfirmed: {
          entry: 'confirmContinueAllTrips',
        },
      },
    },

    completed: {
      initial: 'success',
      on: {
        ABANDON: {
          target: 'idle',
        },
        CANCEL_ALL_TRIPS: 'confirmed.cancelAllTripsConfirmed',
        CONTINUE_ALL_TRIPS: 'confirmed.continueAllTripsConfirmed',
      },
      states: {
        success: {
          entry: 'onSuccess',
        },
        error: {
          entry: 'onError',
        },
      },
    },
  },
})

export type ServiceCancellationMachineState = ReturnType<
  typeof useMachine<typeof serviceCancellationStateMachine>
>[0]

export const ServiceCancellationProvider = ({
  vehicle,
  children,
}: ServiceCancellationProviderProps) => {
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification(true)
  const { openReportDialog } = useAdOperatorReportIo()
  const [initiateServiceCancellationMutation, { loading: isInitiating }] =
    useErrorHandlingMutation(
      useAdVehicleStreamInitiateServiceCancellationMutation,
    )
  const [confirmCancelAllTripsMutation] = useErrorHandlingMutation(
    useAdVehicleStreamConfirmCancelAllTripsMutation,
  )
  const [continueAllTripsMutation] = useErrorHandlingMutation(
    useAdVehicleStreamContinueTripsMutation,
  )
  const emergencyStopAlertId = vehicle.fccCurrentAdAlerts?.alerts.find(
    alert =>
      alert.type === FccAdAlertType.EmergencyStop && alert.status !== 'CLOSED',
  )?.id
  const serviceCancellationAlertExists =
    !!vehicle.fccCurrentAdAlerts?.alerts.find(
      alert =>
        alert.type === FccAdAlertType.ServiceCancellationOngoing &&
        alert.status !== 'CLOSED',
    )

  const [state, send] = useMachine(
    serviceCancellationStateMachine.provide({
      actions: {
        initiateCancellation: assign(() => {
          initiateServiceCancellationMutation({
            variables: {
              vehicleId: vehicle.id,
              incidentId: emergencyStopAlertId,
            },
          })
          return {
            lastConfirmationAction: undefined,
          }
        }),
        confirmCancelAllTrips: assign(() => {
          confirmCancelAllTripsMutation({
            variables: {
              vehicleId: vehicle.id,
            },
          })
          return {
            lastConfirmationAction: 'cancelAllTrips' as const,
          }
        }),
        confirmContinueAllTrips: assign(() => {
          // We can skip this for a user-initiated service cancellation without emergency stop:
          if (emergencyStopAlertId) {
            continueAllTripsMutation({
              variables: {
                vehicleId: vehicle.id,
                incidentId: emergencyStopAlertId,
              },
            })
          } else {
            send({ type: 'ABANDON' })
          }
          return {
            lastConfirmationAction: 'continueAllTrips' as const,
          }
        }),
        onSuccess: ({ context }) => {
          if (context.lastConfirmationAction === 'cancelAllTrips') {
            enqueueMoiaSuccessNotification(
              'Service cancellation has been processed',
            )
            setTimeout(() => {
              openReportDialog({
                vehicleId: vehicle.id,
                creationContext: CreationContext.FleetMapServiceCancellation,
              })
            }, 1000)
          } else if (context.lastConfirmationAction === 'continueAllTrips') {
            enqueueMoiaSuccessNotification(
              'Decision to continue all trips has been processed',
            )
          }
        },
        onError: ({ context }) => {
          console.info('History', context.lastConfirmationAction)
        },
      },
    }),
  )

  // Handle subscription updates
  const { data: subscriptionData } = useErrorHandlingSubscription(
    useAdVehicleStreamServiceCancellationStateSubscription,
    !state.matches('idle') && !isInitiating
      ? {
          variables: { vehicleId: vehicle.id },
        }
      : { skip: true },
  )

  const currentCancellationState =
    subscriptionData?.fccServiceCancellationState.state

  const cancellationError =
    subscriptionData?.fccServiceCancellationState.cancellationError

  useEffect(() => {
    console.info('Subscription update', {
      currentCancellationState,
      cancellationError,
    })
    if (
      currentCancellationState === FccServiceCancellationStateEnum.Completed
    ) {
      if (cancellationError) {
        send({ type: 'CANCELLATION_ERROR' })
      } else {
        send({ type: 'CANCELLATION_SUCCESS' })
      }
    }
  }, [currentCancellationState, cancellationError, send])

  // Initiate cancellation if there is an alert, return to idle if alerts is solved
  useEffect(() => {
    if (serviceCancellationAlertExists) {
      send({ type: 'INITIATE' })
    } else {
      send({ type: 'ABANDON' })
    }
  }, [serviceCancellationAlertExists, send])

  return (
    <ServiceCancellationContext.Provider
      value={{
        state,
        decision: state.context.lastConfirmationAction,
        initiate: () => send({ type: 'INITIATE' }),
        abandon: () => send({ type: 'ABANDON' }),
        cancelAllTrips: () => send({ type: 'CANCEL_ALL_TRIPS' }),
        continueAllTrips: () => send({ type: 'CONTINUE_ALL_TRIPS' }),
      }}
    >
      {children}
    </ServiceCancellationContext.Provider>
  )
}
